/**
 * App shell: navigation and routing.
 *
 * There is no separate wins or slipping page. Both live on the briefing: wins directly
 * under manual entry with their own range filter, slipping split between a group in the
 * task list (tasks) and the "Gone quiet" section (areas and projects). §8.4 and §8.5 both
 * describe things that only work if you meet them while working, and a nav link is exactly
 * the friction that stops that happening.
 *
 * `/settings` used to be `/settings/tokens` — the path §2.2's bootstrap flow originally
 * named — before display preferences (notes-preview line counts) joined token management
 * on the same page. `/settings/tokens` still routes, to a redirect, so an old bookmark or
 * a README a browser still has cached does not just break.
 *
 * Briefing, Structure and Settings keep their original fixed paths (`/`, `/settings/structure`,
 * `/settings`) even though they are now three entries in the same editable page list stage 4
 * introduced (`layout/model.ts`) — an old bookmark, and `/` as the app's home route, both
 * have to keep meaning what they already mean. A page a reader creates gets `/page/:id`
 * instead, since it never had an established path to preserve.
 *
 * Every page can be deleted now, including Briefing (D115/D116). `add`/`notice`/`first-run`
 * were pinned to it and could not move — briefly "fixed" by making the masthead title a
 * permanent link to `/` regardless of the page list, which papered over the real problem by
 * making `/` behave like a page (`wide`, an arrange lock) even when no page list entry named
 * it: a phantom page. Reverted (D118) in favor of making those three portable like
 * everything else, which needs no special-casing here at all — see `sectionRegistry.tsx`.
 */

import { useCallback, useEffect, useState } from "react";

import { startCaptureQueueFlushing } from "./captureQueue";
import { startTaskQueueFlushing, TASK_QUEUE_DELIVERED_EVENT } from "./taskQueue";
import { primeMirror } from "./mirrorPrime";
import { GenericPage } from "./pages/GenericPage";
import { ArrangeLock } from "./layout/editMode";
import { useEditMode } from "./layout/editModeContext";
import { CardModeToggle } from "./components/CardModeToggle";
import {
  createPage,
  deletePage,
  listPages,
  renamePage,
  type PageId,
  type PageMeta,
} from "./layout/model";
import { Footer } from "./components/Footer";
import { Pencil, Plus, Trash } from "./components/icons";
import { pathForPage, routeFor } from "./routes";
import { Link } from "./router";
import { navigate } from "./navigation";
import { usePath } from "./pathContext";
import { randomTagline } from "./taglines";

export function App() {
  const path = usePath();
  const route = routeFor(path);
  const { editing } = useEditMode();

  // Picked once per mount, not once per render — re-rolling it on every page nav would make
  // it flicker under a click instead of reading as a fixed little joke about the name.
  const [tagline] = useState(randomTagline);

  // Bumped by PageNav after creating, renaming or deleting a page — `listPages()` reads
  // straight from localStorage, so nothing else would tell React the nav needs to re-read it.
  const [pagesVersion, setPagesVersion] = useState(0);
  const refreshPages = useCallback(() => setPagesVersion((n) => n + 1), []);
  const pages = listPages();
  void pagesVersion;

  const pagePaths = new Set(pages.map((p) => pathForPage(p.id)));

  // Dev vs. prod, told apart at a glance now that both are in daily use. `import.meta.env.DEV`
  // is a build-time constant — true only under `vite dev`, statically `false` (and dead-code
  // eliminated) in what `vite build` ships, so this cannot leak into the image on `reality`
  // no matter what state it's in. Plain component state rather than `localStorage`: refreshing
  // is "start over," a page nav within the app isn't, and that's exactly what `useState` here
  // already does for free — it survives `Link` navigation (no remount of `App`) but resets on
  // a real reload, with no persistence code required to get either half right.
  const [devSchemeOn, setDevSchemeOn] = useState(true);
  const devScheme = import.meta.env.DEV && devSchemeOn;

  // `App` only renders once `AuthGate` has resolved an authenticated account, which both
  // queues' flushes need (every request is authenticated like everything else) — mounted
  // here rather than in `main.tsx`, where a flush attempt before the gate resolves would
  // just fail on a missing credential and requeue for nothing.
  useEffect(() => startCaptureQueueFlushing(), []);
  useEffect(() => startTaskQueueFlushing(), []);

  // Fill the offline mirror while there is a connection — on load, and again whenever one
  // returns. Background and best-effort; nothing on screen waits for it (§8.2).
  useEffect(() => {
    void primeMirror();
    const onOnline = () => void primeMirror();
    window.addEventListener("online", onOnline);
    window.addEventListener(TASK_QUEUE_DELIVERED_EVENT, onOnline);
    return () => {
      window.removeEventListener("online", onOnline);
      window.removeEventListener(TASK_QUEUE_DELIVERED_EVENT, onOnline);
    };
  }, []);

  return (
    <div className={`app${pagePaths.has(path) ? " wide" : ""}${devScheme ? " dev-scheme" : ""}`}>
      <header className="masthead">
        <h1>
          eFss{devScheme && <span className="badge dev-badge">DEV</span>}
        </h1>
        <span className="tagline">{tagline}</span>
        <span className="date">
          {new Date().toLocaleDateString(undefined, {
            weekday: "long",
            day: "numeric",
            month: "long",
          })}
        </span>
      </header>

      <PageNav
        pages={pages}
        path={path}
        editing={editing}
        showLock={pagePaths.has(path)}
        onChange={refreshPages}
      />

      <main>
        {route.kind === "fixed" && <route.Component />}
        {route.kind === "generic" && <GenericPage id={route.id} />}
        {route.kind === "not-found" && <NotFound />}
      </main>

      <Footer />

      {import.meta.env.DEV && (
        <DevToggle on={devSchemeOn} onToggle={() => setDevSchemeOn((on) => !on)} />
      )}
    </div>
  );
}

/**
 * Dev-only — never rendered from a production build (see `App`'s `import.meta.env.DEV`
 * guard). Turns the dev colour scheme off to preview what a reader on `reality` actually
 * sees, without switching servers to check.
 */
function DevToggle({ on, onToggle }: { on: boolean; onToggle: () => void }) {
  return (
    <section className="panel dev-toggle">
      <p className="muted small" style={{ marginTop: 0 }}>
        Dev server
      </p>
      <button type="button" className="btn small" onClick={onToggle}>
        {on ? "Preview prod colors" : "Back to dev colors"}
      </button>
    </section>
  );
}

/**
 * The nav, and — while unlocked — the page-management controls that go with a stored,
 * editable page list: rename or delete any page, including the three built-in ones, plus
 * add a new one. Renaming and deleting are per-page rather than page-scoped, because the
 * nav is the one place every page is listed at once; there is nowhere else a "delete this
 * page" control could live that would not mean visiting the page first.
 */
export function PageNav({
  pages,
  path,
  editing,
  showLock,
  onChange,
}: {
  pages: PageMeta[];
  path: string;
  editing: boolean;
  showLock: boolean;
  onChange: () => void;
}) {
  const [renaming, setRenaming] = useState<PageId | null>(null);
  const [draftName, setDraftName] = useState("");

  function startRename(page: PageMeta) {
    setRenaming(page.id);
    setDraftName(page.name);
  }

  function commitRename() {
    if (renaming) renamePage(renaming, draftName);
    setRenaming(null);
    onChange();
  }

  function addPage() {
    const page = createPage("New page");
    onChange();
    navigate(pathForPage(page.id));
    startRename(page);
  }

  function removePage(page: PageMeta) {
    if (
      !window.confirm(
        `Delete the page "${page.name}"?\n\n` +
          "Any card that was only here becomes available to add elsewhere - nothing is " +
          "lost, only unplaced. This only affects this device.",
      )
    ) {
      return;
    }
    // Any page can go now, including the built-in three — deletePage never leaves the list
    // empty, so there is always somewhere left to read back for where to send the reader if
    // the page they just deleted is the one they were looking at.
    deletePage(page.id);
    onChange();
    if (path === pathForPage(page.id)) navigate(pathForPage(listPages()[0].id));
  }

  return (
    <nav className="nav">
      {pages.map((page) =>
        editing && renaming === page.id ? (
          <input
            key={page.id}
            className="input small"
            style={{ width: "10rem" }}
            aria-label={`Rename ${page.name}`}
            value={draftName}
            autoFocus
            onChange={(e) => setDraftName(e.target.value)}
            onBlur={commitRename}
            onKeyDown={(event) => {
              if (event.key === "Enter") {
                event.preventDefault();
                commitRename();
              }
              if (event.key === "Escape") setRenaming(null);
            }}
          />
        ) : (
          <span key={page.id} className={`row nav-page${editing ? " nav-page-editing" : ""}`}>
            <Link to={pathForPage(page.id)}>{page.name}</Link>
            {editing && (
              <span className="row nav-page-controls">
                <button
                  type="button"
                  className="btn ghost small"
                  aria-label={`Rename ${page.name}`}
                  title="Rename this page"
                  onClick={() => startRename(page)}
                >
                  <Pencil size={12} />
                </button>
                <button
                  type="button"
                  className="btn ghost small"
                  aria-label={`Delete ${page.name}`}
                  title="Delete this page"
                  onClick={() => removePage(page)}
                >
                  <Trash size={12} />
                </button>
              </span>
            )}
          </span>
        ),
      )}

      {editing && (
        <button
          type="button"
          className="btn ghost small"
          aria-label="Add a new page"
          title="Add a new page"
          onClick={addPage}
        >
          <Plus size={12} /> page
        </button>
      )}

      <span className="nav-toggles">
        <CardModeToggle />
        {showLock && <ArrangeLock />}
      </span>
    </nav>
  );
}

function NotFound() {
  return (
    <section className="panel">
      <h2>Not a page</h2>
      <p className="muted">
        Nothing lives at this address. <Link to="/">Back to the briefing</Link>.
      </p>
    </section>
  );
}
